// AI Automation policy — SUPER_ADMIN-only editor for how far an AI
// assistant may automate crucial (user-visible) actions. Each category
// maps to a mode: auto / confirm / double_confirm / approve / block.
// User-visible categories can NEVER be set to `auto` (the option is
// disabled here and rejected server-side + coerced in mcp-service) — a
// human step is always required before anything reaches end users.
// Data: GET/PUT /admin/ai/policy.
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Select } from "@/components/ui/Select";
import { Skeleton } from "@/components/ui/Skeleton";

type Mode = "auto" | "confirm" | "double_confirm" | "approve" | "block";

interface Policy {
  modes: Record<string, Mode>;
  require_different_approver?: boolean;
}

// Ordered category metadata. `public` categories affect what end users
// see and may never be `auto`.
const CATEGORIES: { key: string; label: string; help: string; public: boolean }[] = [
  { key: "publish", label: "Publish market", help: "Make a single market visible to users.", public: true },
  { key: "bulk_publish", label: "Publish all", help: "Publish/unpublish every market under an event.", public: true },
  { key: "resolve", label: "Resolve", help: "Settle a market to a winning outcome (irreversible).", public: true },
  { key: "cancel", label: "Cancel", help: "Void a market and refund positions (irreversible).", public: true },
  { key: "delete", label: "Delete", help: "Hard-delete a resolved/cancelled market.", public: false },
  { key: "live_edit", label: "Live edit", help: "Edit fees / window / icon on a published entity.", public: true },
];

const ALL_MODES: { value: Mode; label: string }[] = [
  { value: "auto", label: "Auto — AI applies directly" },
  { value: "confirm", label: "Confirm — one confirmation" },
  { value: "double_confirm", label: "Double confirm — two confirmations" },
  { value: "approve", label: "Approve — human approves in backoffice" },
  { value: "block", label: "Block — disabled for AI" },
];

export const Route = createFileRoute("/_app/ai/policy")({
  component: PolicyPage,
});

function PolicyPage() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["ai", "policy"],
    queryFn: () => api<Policy>("/admin/ai/policy"),
  });

  const [modes, setModes] = useState<Record<string, Mode>>({});
  const [diffApprover, setDiffApprover] = useState(false);

  useEffect(() => {
    if (q.data) {
      setModes(q.data.modes ?? {});
      setDiffApprover(!!q.data.require_different_approver);
    }
  }, [q.data]);

  const save = useMutation({
    mutationFn: (body: Policy) => api("/admin/ai/policy", { method: "PUT", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ai", "policy"] }),
    meta: { successMessage: "Automation policy saved." },
  });

  const setMode = (cat: string, mode: Mode) => setModes((m) => ({ ...m, [cat]: mode }));

  const dirty =
    q.data != null &&
    (JSON.stringify(modes) !== JSON.stringify(q.data.modes ?? {}) ||
      diffApprover !== !!q.data.require_different_approver);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-semibold text-t-1">AI Automation Policy</h1>
        <p className="text-sm text-muted">
          Controls how far a connected AI assistant may automate crucial actions. Anything users
          can see always requires at least one human step — those categories can't be set to
          Auto.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Per-action controls</CardTitle>
        </CardHeader>
        <CardBody>
          {q.isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : (
            <div className="space-y-4">
              {CATEGORIES.map((c) => (
                <div
                  key={c.key}
                  className="flex flex-col gap-2 border-b border-line pb-4 last:border-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-t-1">{c.label}</span>
                      {c.public && <Badge tone="warning">user-visible</Badge>}
                    </div>
                    <div className="text-xs text-muted">{c.help}</div>
                  </div>
                  <div className="w-full sm:w-72">
                    <Select
                      value={modes[c.key] ?? ""}
                      onChange={(e) => setMode(c.key, e.target.value as Mode)}
                      // user-visible categories can't be Auto — omit it entirely
                      options={ALL_MODES.filter(
                        (m) => !(c.public && m.value === "auto"),
                      ).map((m) => ({ value: m.value, label: m.label }))}
                    />
                  </div>
                </div>
              ))}

              <label className="flex items-center gap-2 pt-2 text-sm text-t-2">
                <input
                  type="checkbox"
                  checked={diffApprover}
                  onChange={(e) => setDiffApprover(e.target.checked)}
                />
                Require a different approver (four-eyes) for approve-mode actions
              </label>
            </div>
          )}
        </CardBody>
      </Card>

      <div className="flex items-center gap-3">
        <Button
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate({ modes, require_different_approver: diffApprover })}
        >
          {save.isPending ? "Saving…" : "Save policy"}
        </Button>
        {save.isError && (
          <span className="text-sm text-red-400">{(save.error as Error).message}</span>
        )}
      </div>
    </div>
  );
}
